import { inject, Injectable } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { catchError, map, Observable, of, take, tap } from 'rxjs';
import { ConsumptionEntity, ConsumptionsPerDevice } from '@data-terminal/shared-models/consumption.model';
import { ConsumptionsApiService } from '@data-terminal/feature-workstation-details/services/consumptions-api/consumptions-api.service';
import {
    BatchInformationDialogComponent,
    RequestBatchInformationDialogComponent,
} from '@data-terminal/feature-dialogs/dialogs';

export interface BatchInformationDialogData {
    jobId: string;
    batchInformation: ConsumptionsPerDevice;
}

@Injectable({
    providedIn: 'root',
})
export class BatchInformationDialogService {
    readonly #dialog = inject(MatDialog);
    readonly #consumptionsApiService = inject(ConsumptionsApiService);

    public openRequestBatchInformationDialog(): void {
        this.#dialog.open<RequestBatchInformationDialogComponent>(RequestBatchInformationDialogComponent);
    }

    public openBatchInformationDialog(jobId: string, batchInformation: ConsumptionsPerDevice): void {
        this.#dialog.open<BatchInformationDialogComponent, BatchInformationDialogData>(
            BatchInformationDialogComponent,
            {
                width: '680px',
                maxHeight: '99vh',
                data: {
                    jobId,
                    batchInformation,
                },
                autoFocus: false,
            }
        );
    }

    public requestBatchInformation(jobId: string): Observable<ConsumptionsPerDevice> {
        return this.#consumptionsApiService.getBatchInformationForJobIdPerDevice(jobId).pipe(
            map((batchInformation: ConsumptionsPerDevice) => {
                const machineIds = Object.keys(batchInformation);
                const sortedBatchInformation: ConsumptionsPerDevice = {};
                machineIds.forEach((machineId) => {
                    sortedBatchInformation[machineId] = [...batchInformation[machineId]].sort(
                        (batchInformationA: ConsumptionEntity, batchInformationB: ConsumptionEntity) =>
                            (batchInformationA.updatedAt ?? '').localeCompare(batchInformationB.updatedAt ?? '')
                    );
                });
                return sortedBatchInformation;
            }),
            take(1),
            tap((batchInformation: ConsumptionsPerDevice) => this.openBatchInformationDialog(jobId, batchInformation)),
            catchError(() => of({}))
        );
    }
}
